import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { diagnosticService } from '../diagnosticService';
import type { DiagnosticReceipt } from '../diagnosticService';
import { normalizeMobile, receiptPatientMobile } from '../../../utils/patientMobile';

export function useDiagnosticCategories(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['diagnostic-categories', page, limit, search],
    queryFn: () => diagnosticService.getCategories(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActiveDiagnosticCategories() {
  return useQuery({
    queryKey: ['diagnostic-categories', 'active'],
    queryFn: () => diagnosticService.getActiveCategories(),
    staleTime: 30000,
  });
}

export function useCreateDiagnosticCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-categories'] });
    },
  });
}

export function useUpdateDiagnosticCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-categories'] });
    },
  });
}

export function useDeleteDiagnosticCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-categories'] });
    },
  });
}

export function useDiagnosticCategory(id?: string) {
  return useQuery({
    queryKey: ['diagnostic-category', id],
    queryFn: () => diagnosticService.getCategory(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

/** Bulk-delete categories as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDiagnosticCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await diagnosticService.deleteCategory(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-categories'] });
    },
  });
}

export function useSpecimenTypes(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['specimen-types', page, limit, search],
    queryFn: () => diagnosticService.getSpecimenTypes(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActiveSpecimenTypes() {
  return useQuery({
    queryKey: ['specimen-types', 'active'],
    queryFn: () => diagnosticService.getActiveSpecimenTypes(),
    staleTime: 30000,
  });
}

export function useCreateSpecimenType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createSpecimenType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specimen-types'] });
    },
  });
}

export function useUpdateSpecimenType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateSpecimenType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specimen-types'] });
    },
  });
}

export function useDeleteSpecimenType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteSpecimenType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specimen-types'] });
    },
  });
}

export function useSpecimenType(id?: string) {
  return useQuery({
    queryKey: ['specimen-type', id],
    queryFn: () => diagnosticService.getSpecimenType(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

/** Bulk-delete specimen types as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteSpecimenTypes() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await diagnosticService.deleteSpecimenType(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specimen-types'] });
    },
  });
}

export function useDiagnosticDepartments(page = 1, limit = 20, search?: string, all = false) {
  return useQuery({
    queryKey: ['diagnostic-departments', page, limit, search, all],
    queryFn: () => diagnosticService.getDepartments(page, limit, search, all),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useDiagnosticDepartment(id?: string) {
  return useQuery({
    queryKey: ['diagnostic-department', id],
    queryFn: () => diagnosticService.getDepartment(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateDiagnosticDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-departments'] });
    },
  });
}

export function useUpdateDiagnosticDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateDepartment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-departments'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-department', variables.id] });
    },
  });
}

export function useDeleteDiagnosticDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-departments'] });
    },
  });
}

/** Bulk-delete departments as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDiagnosticDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await diagnosticService.deleteDepartment(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-departments'] });
    },
  });
}

/** Paginated + searchable trashed departments (the Trash page). */
export function useTrashedDiagnosticDepartments(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['diagnostic-departments', 'trash', page, limit, search],
    queryFn: () => diagnosticService.getTrashedDepartments(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useRestoreDiagnosticDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.restoreDepartments(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-departments'] });
    },
  });
}

export function usePermanentlyDeleteDiagnosticDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.permanentlyDeleteDepartments(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-departments'] });
    },
  });
}

export function useDiagnosticRooms(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['diagnostic-rooms', page, limit, search],
    queryFn: () => diagnosticService.getRooms(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useAllDiagnosticRooms() {
  return useQuery({
    queryKey: ['diagnostic-rooms', 'all'],
    queryFn: () => diagnosticService.getRooms(1, 1000),
    staleTime: 30000,
  });
}

export function useDiagnosticRoom(id?: string) {
  return useQuery({
    queryKey: ['diagnostic-room', id],
    queryFn: () => diagnosticService.getRoom(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateDiagnosticRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-rooms'] });
    },
  });
}

export function useUpdateDiagnosticRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateRoom(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-rooms'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-room', variables.id] });
    },
  });
}

export function useDeleteDiagnosticRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteRoom(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-rooms'] });
    },
  });
}

/** Bulk-delete rooms as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDiagnosticRooms() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await diagnosticService.deleteRoom(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-rooms'] });
    },
  });
}

export function useFloors(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['floors', page, limit, search],
    queryFn: () => diagnosticService.getFloors(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useAllFloors() {
  return useQuery({
    queryKey: ['floors', 'all'],
    queryFn: () => diagnosticService.getAllFloors(),
    staleTime: 30000,
  });
}

export function useFloor(id?: string) {
  return useQuery({
    queryKey: ['floor', id],
    queryFn: () => diagnosticService.getFloor(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateFloor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createFloor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floors'] });
    },
  });
}

export function useUpdateFloor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateFloor(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['floors'] });
      queryClient.invalidateQueries({ queryKey: ['floor', variables.id] });
    },
  });
}

export function useDeleteFloor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteFloor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floors'] });
    },
  });
}

/** Bulk-delete floors as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteFloors() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await diagnosticService.deleteFloor(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floors'] });
    },
  });
}

export function useDiagnosticTests(page = 1, limit = 20, search?: string, sortBy?: string, sortOrder?: 'ASC' | 'DESC') {
  return useQuery({
    queryKey: ['diagnostic-tests', page, limit, search, sortBy, sortOrder],
    queryFn: () => diagnosticService.getTests(page, limit, search, sortBy, sortOrder),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActiveDiagnosticTests() {
  return useQuery({
    queryKey: ['diagnostic-tests', 'active'],
    queryFn: () => diagnosticService.getActiveTests(),
    staleTime: 30000,
  });
}

/**
 * Server-side paginated list of active tests for the receipt form, optionally
 * filtered by category and search term. `categoryId='ALL'` or undefined means
 * no category filter.
 */
export function useAvailableDiagnosticTests(
  page = 1,
  limit = 20,
  categoryId?: string,
  search?: string,
) {
  const resolvedCategoryId = categoryId && categoryId !== 'ALL' ? categoryId : undefined;
  return useQuery({
    queryKey: ['diagnostic-tests', 'available', page, limit, resolvedCategoryId, search],
    queryFn: () =>
      diagnosticService.getTests(page, limit, search, 'name', 'ASC', {
        categoryId: resolvedCategoryId,
        status: 'ACTIVE',
      }),
    staleTime: 30000,
    placeholderData: (previous) => previous,
  });
}

export function useDiagnosticTestsByDepartment(departmentId?: string) {
  return useQuery({
    queryKey: ['diagnostic-tests', 'department', departmentId],
    queryFn: () => diagnosticService.getTestsByDepartment(departmentId!),
    enabled: !!departmentId,
    staleTime: 30000,
  });
}

export function useDiagnosticTest(id?: string) {
  return useQuery({
    queryKey: ['diagnostic-test', id],
    queryFn: () => diagnosticService.getTest(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateDiagnosticTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createTest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-tests'] });
    },
  });
}

export function useUpdateDiagnosticTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateTest(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-tests'] });
    },
  });
}

export function useDeleteDiagnosticTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteTest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-tests'] });
    },
  });
}

/** Bulk-delete tests as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDiagnosticTests() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await diagnosticService.deleteTest(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-tests'] });
    },
  });
}

/** Bulk-update tests (e.g. activate/deactivate) as a single silent mutation. */
export function useBulkUpdateDiagnosticTests() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async ({ ids, data }: { ids: string[]; data: any }) => {
      for (const id of ids) {
        await diagnosticService.updateTest(id, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-tests'] });
    },
  });
}

/** Paginated + searchable trashed tests (the Trash page). */
export function useTrashedDiagnosticTests(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['diagnostic-tests', 'trash', page, limit, search],
    queryFn: () => diagnosticService.getTrashedTests(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

/** Restore selected trashed tests. */
export function useRestoreDiagnosticTests() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.restoreTests(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-tests'] });
    },
  });
}

/** Permanently delete selected trashed tests. */
export function usePermanentlyDeleteDiagnosticTests() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.permanentlyDeleteTests(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-tests'] });
    },
  });
}

export function useDiagnosticAccessories(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['diagnostic-accessories', page, limit, search],
    queryFn: () => diagnosticService.getAccessories(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActiveAccessories() {
  return useQuery({
    queryKey: ['diagnostic-accessories', 'active'],
    queryFn: () => diagnosticService.getActiveAccessories(),
    staleTime: 30000,
  });
}

export function useDiagnosticAccessory(id?: string) {
  return useQuery({
    queryKey: ['diagnostic-accessory', id],
    queryFn: () => diagnosticService.getAccessory(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateDiagnosticAccessory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createAccessory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessories'] });
    },
  });
}

export function useUpdateDiagnosticAccessory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateAccessory(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessories'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessory', variables.id] });
    },
  });
}

export function useDeleteDiagnosticAccessory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteAccessory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessories'] });
    },
  });
}

/** Bulk-delete accessories as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDiagnosticAccessories() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await diagnosticService.deleteAccessory(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessories'] });
    },
  });
}

/** Bulk-update accessories (e.g. activate/deactivate) as a single silent mutation. */
export function useBulkUpdateDiagnosticAccessories() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async ({ ids, data }: { ids: string[]; data: any }) => {
      for (const id of ids) {
        await diagnosticService.updateAccessory(id, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessories'] });
    },
  });
}

/** Paginated + searchable trashed accessories (the Trash page). */
export function useTrashedDiagnosticAccessories(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['diagnostic-accessories', 'trash', page, limit, search],
    queryFn: () => diagnosticService.getTrashedAccessories(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

/** Restore selected trashed accessories. */
export function useRestoreDiagnosticAccessories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.restoreAccessories(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessories'] });
    },
  });
}

/** Permanently delete selected trashed accessories. */
export function usePermanentlyDeleteDiagnosticAccessories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.permanentlyDeleteAccessories(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-accessories'] });
    },
  });
}

export function useDiagnosticReceipts(
  page = 1,
  limit = 20,
  filters?: { search?: string; status?: string; paymentMode?: string; paymentStatus?: string; isReferral?: 'true' | 'false'; dateFrom?: string; dateTo?: string; dateBasis?: 'document' | 'settled'; departmentId?: string; patientId?: string },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['diagnostic-receipts', page, limit, filters],
    queryFn: () => diagnosticService.getReceipts(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
    enabled: options.enabled ?? true,
  });
}

/** Diagnostic receipt totals (billed / paid / due) for a single patient. */
export function usePatientReceiptSummary(
  patientId?: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['patient-receipt-summary', patientId],
    queryFn: () => diagnosticService.getPatientReceiptSummary(patientId!),
    enabled: Boolean(patientId) && (options.enabled ?? true),
    staleTime: 30000,
  });
}

/**
 * Other diagnostic receipts that still carry a balance (Due or partially paid)
 * for the same patient, newest balance first.
 *
 * The patient is identified by MOBILE NUMBER, never by name: the counter often
 * has the same person stored twice (quick entry + full registration, or a
 * re-registration with a different spelling), and the mobile is what is
 * actually verified at the desk. When the record has no mobile the lookup falls
 * back to `patientId` so the check still works.
 *
 * `excludeReceiptId` keeps the receipt currently open out of the result.
 */
export function usePatientDueReceipts(
  patient?: { id?: string | null; mobileNumber?: string | null; mobile?: string | null },
  options: { excludeReceiptId?: string; enabled?: boolean } = {},
) {
  const patientId = patient?.id || '';
  const mobile = normalizeMobile(receiptPatientMobile(patient));
  const excludeReceiptId = options.excludeReceiptId;

  return useQuery({
    queryKey: ['patient-due-receipts', patientId || null, mobile || null, excludeReceiptId || null],
    queryFn: async () => {
      // Search by the mobile digits rather than the patient id so a duplicate
      // patient record holding the same number is picked up too. The backend
      // compares digits only, so formatting never changes the result.
      const filters = mobile ? { patientMobile: mobile, patientId } : { patientId };
      const response = await diagnosticService.getReceipts(1, 50, filters);
      return (response.data || [])
        .filter((receipt: DiagnosticReceipt) => {
          if (receipt.id === excludeReceiptId) return false;
          // A refunded receipt is settled — there is nothing to collect on it.
          if (receipt.status === 'REFUNDED') return false;
          if (!(Number(receipt.dueAmount) > 0)) return false;
          // Identity is the mobile number (falling back to the id when the
          // patient has none). The name is deliberately never compared.
          if (mobile) {
            return (
              normalizeMobile(receiptPatientMobile(receipt.patient)) === mobile ||
              receipt.patientId === patientId
            );
          }
          return receipt.patientId === patientId;
        })
        // Oldest balance first, so the prompt leads with the receipt that has
        // been outstanding the longest.
        .sort(
          (a: DiagnosticReceipt, b: DiagnosticReceipt) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
    },
    enabled: (options.enabled ?? true) && Boolean(patientId || mobile),
    staleTime: 15000,
  });
}

export function useDiagnosticReceipt(id?: string) {
  return useQuery({
    queryKey: ['diagnostic-receipt', id],
    queryFn: () => diagnosticService.getReceipt(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useReceiptSpecimens(receiptId?: string) {
  return useQuery({
    queryKey: ['receipt-specimens', receiptId],
    queryFn: () => diagnosticService.getReceiptSpecimens(receiptId!),
    enabled: !!receiptId,
    staleTime: 15000,
  });
}

export function usePlanReceiptSpecimens() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (receiptId: string) => diagnosticService.planReceiptSpecimens(receiptId),
    onSuccess: (_, receiptId) => {
      queryClient.invalidateQueries({ queryKey: ['receipt-specimens', receiptId] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-receipt', receiptId] });
    },
  });
}

function invalidateSpecimenQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['specimens'] });
  queryClient.invalidateQueries({ queryKey: ['receipt-specimens'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-routings'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-dashboard'] });
}

export function useSpecimens(
  filters?: {
    status?: string;
    departmentId?: string;
    roomId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  },
  page = 1,
  limit = 20,
) {
  return useQuery({
    queryKey: ['specimens', filters, page, limit],
    queryFn: () => diagnosticService.getSpecimens({ ...filters, page, limit }),
    placeholderData: (previous) => previous,
    staleTime: 15000,
  });
}

export function useSpecimen(id?: string) {
  return useQuery({
    queryKey: ['specimen', id],
    queryFn: () => diagnosticService.getSpecimen(id!),
    enabled: !!id,
    staleTime: 15000,
  });
}

export function useScanSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (barcode: string) => diagnosticService.scanSpecimen(barcode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specimens'] });
    },
  });
}

export function useCollectSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.collectSpecimen(id),
    onSuccess: () => invalidateSpecimenQueries(queryClient),
  });
}

export function useReceiveSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.receiveSpecimen(id),
    onSuccess: () => invalidateSpecimenQueries(queryClient),
  });
}

export function useProcessSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.processSpecimen(id),
    onSuccess: () => invalidateSpecimenQueries(queryClient),
  });
}

export function useCompleteSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.completeSpecimen(id),
    onSuccess: () => invalidateSpecimenQueries(queryClient),
  });
}

export function useRejectSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => diagnosticService.rejectSpecimen(id, reason),
    onSuccess: () => invalidateSpecimenQueries(queryClient),
  });
}

export function useRedrawSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.redrawSpecimen(id),
    onSuccess: () => invalidateSpecimenQueries(queryClient),
  });
}

export function useDeleteSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteSpecimen(id),
    onSuccess: () => invalidateSpecimenQueries(queryClient),
  });
}

export function useCreateDiagnosticReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createReceipt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['patient-due-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateDiagnosticReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => diagnosticService.updateReceipt(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['patient-due-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-receipt', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-distributions'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-distributions-summary'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/** Collect an outstanding balance against a diagnostic receipt (dated today). */
export function useRecordDiagnosticReceiptPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      diagnosticService.recordReceiptPayment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['patient-due-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-receipt', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
    },
  });
}

export function useDeleteDiagnosticReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => diagnosticService.deleteReceipt(id),
    onSuccess: () => invalidateReceiptQueries(queryClient),
  });
}

/**
 * Merge several of a patient's unpaid receipts into one. The target receipt is
 * edited in place (its items and balance change), so every receipt-derived
 * cache is refreshed once the call returns.
 */
export function useMergeDiagnosticReceipts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      targetReceiptId,
      sourceReceiptIds,
    }: {
      targetReceiptId: string;
      sourceReceiptIds: string[];
    }) => diagnosticService.mergeReceipts(targetReceiptId, sourceReceiptIds),
    onSuccess: () => invalidateReceiptQueries(queryClient),
  });
}

/** Shared cache invalidation for receipt + trash views. */
function invalidateReceiptQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
  queryClient.invalidateQueries({ queryKey: ['patient-due-receipts'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts-trash'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-receipt'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-routings'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-dashboard'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions-summary'] });
  queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

/** Paginated + filtered trashed receipts. */
export function useTrashedDiagnosticReceipts(
  page = 1,
  limit = 20,
  filters?: { search?: string; status?: string; paymentMode?: string; paymentStatus?: string; dateFrom?: string; dateTo?: string; departmentId?: string },
) {
  return useQuery({
    queryKey: ['diagnostic-receipts-trash', page, limit, filters],
    queryFn: () => diagnosticService.getTrashedReceipts(page, limit, filters),
    staleTime: 15000,
    placeholderData: (previous) => previous,
  });
}

/** Move selected receipts to the trash. */
export function useTrashDiagnosticReceipts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.trashReceipts(ids),
    onSuccess: () => invalidateReceiptQueries(queryClient),
  });
}

/** Restore selected trashed receipts. */
export function useRestoreDiagnosticReceipts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.restoreReceipts(ids),
    onSuccess: () => invalidateReceiptQueries(queryClient),
  });
}

/** Permanently delete selected trashed receipts. */
export function usePermanentlyDeleteDiagnosticReceipts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.permanentDeleteReceipts(ids),
    onSuccess: () => invalidateReceiptQueries(queryClient),
  });
}

export function useDiagnosticDashboard(departmentId?: string) {
  return useQuery({
    queryKey: ['diagnostic-dashboard', departmentId],
    queryFn: () => diagnosticService.getDepartmentDashboard(departmentId!),
    enabled: !!departmentId,
    staleTime: 15000,
  });
}

export function useDiagnosticRouting(routingId?: string) {
  return useQuery({
    queryKey: ['diagnostic-routing', routingId],
    queryFn: () => diagnosticService.getRouting(routingId!),
    enabled: !!routingId,
    staleTime: 15000,
  });
}

export function useAllDiagnosticRoutings(filters?: { status?: string; departmentId?: string; categoryId?: string; dateFrom?: string; dateTo?: string; search?: string }) {
  return useQuery({
    queryKey: ['diagnostic-routings', filters],
    queryFn: () => diagnosticService.getAllRoutings(filters),
    staleTime: 15000,
  });
}

/** Paginated + API-filtered orders (routings) list. */
export function useDiagnosticRoutingsPaginated(
  page = 1,
  limit = 20,
  filters?: { status?: string; departmentId?: string; categoryId?: string; dateFrom?: string; dateTo?: string; search?: string },
) {
  return useQuery({
    queryKey: ['diagnostic-routings', 'paginated', page, limit, filters],
    queryFn: () => diagnosticService.getRoutingsPaginated(page, limit, filters),
    staleTime: 15000,
    placeholderData: (previous) => previous,
  });
}

/** Paginated + filtered trashed orders (routings). */
export function useTrashedDiagnosticRoutings(
  page = 1,
  limit = 20,
  filters?: { search?: string; departmentId?: string; dateFrom?: string; dateTo?: string },
) {
  return useQuery({
    queryKey: ['diagnostic-routings-trash', page, limit, filters],
    queryFn: () => diagnosticService.getTrashedRoutings(page, limit, filters),
    staleTime: 15000,
    placeholderData: (previous) => previous,
  });
}

function invalidateRoutingQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['diagnostic-routings'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-routings-trash'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-dashboard'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions-summary'] });
  queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

/** Move selected orders (routings) to the trash. */
export function useTrashDiagnosticRoutings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.trashRoutings(ids),
    onSuccess: () => invalidateRoutingQueries(queryClient),
  });
}

/** Restore selected trashed orders (routings). */
export function useRestoreDiagnosticRoutings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.restoreRoutings(ids),
    onSuccess: () => invalidateRoutingQueries(queryClient),
  });
}

/** Permanently delete selected trashed orders (routings). */
export function usePermanentlyDeleteDiagnosticRoutings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.permanentDeleteRoutings(ids),
    onSuccess: () => invalidateRoutingQueries(queryClient),
  });
}

export function useUpdateRoutingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ routingId, data }: { routingId: string; data: any }) => diagnosticService.updateRoutingStatus(routingId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-routings'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-routing', variables.routingId] });
      queryClient.invalidateQueries({ queryKey: ['revenue-distributions'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-distributions-summary'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function invalidateRevenueQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions-summary'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions-trash'] });
  queryClient.invalidateQueries({ queryKey: ['doctor-payments'] });
  queryClient.invalidateQueries({ queryKey: ['payee-outstanding'] });
  queryClient.invalidateQueries({ queryKey: ['payout-vouchers'] });
  queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useRevenueDistributions(params?: {
  status?: string;
  sourceType?: string;
  departmentId?: string;
  doctorId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['revenue-distributions', params],
    queryFn: () => diagnosticService.getRevenueDistributions(params),
    staleTime: 30000,
    placeholderData: (previous) => previous,
  });
}

export function useRevenueDistributionSummary(params?: {
  status?: string;
  doctorId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['revenue-distributions-summary', params],
    queryFn: () => diagnosticService.getRevenueDistributionSummary(params),
    staleTime: 30000,
    placeholderData: (previous) => previous,
  });
}

/** Paginated + filtered trashed revenue distributions. */
export function useTrashedRevenueDistributions(page = 1, limit = 20, filters?: { search?: string }) {
  return useQuery({
    queryKey: ['revenue-distributions-trash', page, limit, filters],
    queryFn: () => diagnosticService.getTrashedRevenueDistributions(page, limit, filters),
    staleTime: 15000,
    placeholderData: (previous) => previous,
  });
}

export function useTrashRevenueDistributions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.trashRevenueDistributions(ids),
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

export function useTrashRevenueDistributionsByDoctors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (doctorIds: string[]) => diagnosticService.trashRevenueDistributionsByDoctors(doctorIds),
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

export function useRestoreRevenueDistributions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.restoreRevenueDistributions(ids),
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

export function usePermanentlyDeleteRevenueDistributions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.permanentDeleteRevenueDistributions(ids),
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

export function useSettleRevenueDistribution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      settledById,
      method,
      notes,
    }: {
      id: string;
      settledById?: string;
      method?: string;
      notes?: string;
    }) => diagnosticService.settleRevenueDistribution(id, settledById, { method, notes }),
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

export function useSettleRevenueDistributionsBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.settleRevenueDistributionsBulk,
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

export function useDoctorPayments(params?: {
  doctorId?: string;
  payeeType?: 'DOCTOR' | 'REFERRAL';
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['doctor-payments', params],
    queryFn: () => diagnosticService.getDoctorPayments(params),
    staleTime: 30000,
    placeholderData: (previous) => previous,
  });
}

export function useCreateLegacyDoctorPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.createLegacyDoctorPayment,
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

// ── Payee-agnostic payout facade (ADR-015) ──────────────────────────────────

/** Outstanding payables — one ledger of payees (doctors + referral partners). */
export function usePayeeOutstanding(params?: {
  payeeType?: 'DOCTOR' | 'REFERRAL';
  payeeId?: string;
  status?: string;
  source?: 'OPD' | 'IPD';
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['payee-outstanding', params],
    queryFn: () => diagnosticService.getPayeeOutstanding(params),
    staleTime: 30000,
    // Payouts are money owed: always pull a fresh ledger when the screen is
    // opened, even if a bill was settled from another tab or another user.
    refetchOnMount: 'always',
    placeholderData: (previous) => previous,
  });
}

/** Accrual history — the receipts/consultations behind one payee's balance. */
export function usePayeeAccruals(
  params: {
    payeeType?: 'DOCTOR' | 'REFERRAL';
    payeeId?: string;
    status?: string;
    source?: 'OPD' | 'IPD';
    outstandingOnly?: boolean;
    page?: number;
    limit?: number;
  },
  /** Only issue the request while the details sheet is actually open. */
  enabled = true,
) {
  return useQuery({
    queryKey: ['payee-accruals', params],
    queryFn: () =>
      diagnosticService.getPayeeAccruals({
        payeeType: params.payeeType ?? 'DOCTOR',
        payeeId: params.payeeId as string,
        status: params.status,
        source: params.source,
        outstandingOnly: params.outstandingOnly,
        page: params.page,
        limit: params.limit,
      }),
    enabled: enabled && !!params.payeeId,
    staleTime: 30000,
    refetchOnMount: 'always',
    placeholderData: (previous) => previous,
  });
}

/** Payout vouchers — every issued `DP-` / `RP-` voucher in one ledger. */
export function usePayoutVouchers(params?: {
  payeeType?: 'DOCTOR' | 'REFERRAL';
  payeeId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['payout-vouchers', params],
    queryFn: () => diagnosticService.getPayoutVouchers(params),
    staleTime: 30000,
    placeholderData: (previous) => previous,
  });
}

/** A single payout voucher with its settled line items (opened in a modal). */
export function usePayoutVoucher(id?: string) {
  return useQuery({
    queryKey: ['payout-voucher', id],
    queryFn: () => diagnosticService.getPayoutVoucher(id as string),
    enabled: !!id,
    staleTime: 30000,
  });
}

/** Settle a payee (doctor or referral partner) into numbered vouchers. */
export function useSettlePayouts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.settlePayouts,
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

/** Settle several payees in one request (one voucher per payee). */
export function useSettlePayeesBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.settlePayeesBulk,
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

/** Move a payee's outstanding accruals to the trash (reversible). */
export function useRemoveOutstandingByPayees() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: diagnosticService.removeOutstandingByPayees,
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}

/** Move issued payout vouchers to the trash, reversing their payouts. */
export function useTrashPayoutVouchers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => diagnosticService.trashPayoutVouchers(ids),
    onSuccess: () => invalidateRevenueQueries(queryClient),
  });
}
